/**
 * Migration utility for checklist templates
 * 
 * This module provides a browser-compatible migration function that can be
 * executed from the browser console to populate Firebase with checklist templates.
 * 
 * Usage:
 *   In browser console: window.migrateChecklistTemplates()
 *   or: migrateChecklistTemplates()
 */

import { Timestamp } from 'firebase/firestore';
import type { LoanType } from '@/types';
import type {
  ChecklistTemplate,
  ChecklistCategory,
  ChecklistItem as ChecklistItemTemplate,
} from '@/types/checklist';
import { createChecklistTemplate } from '@/services/checklistService';

// ============================================================================
// CHECKLIST TEMPLATE DEFINITIONS
// ============================================================================

/**
 * Home Loan / LAP Checklist Template
 */
const homeLoanChecklist: Omit<ChecklistTemplate, 'id' | 'createdAt' | 'updatedAt'> = {
  loanType: 'home_loan' as LoanType,
  name: 'Home Loan Document Checklist',
  description: 'Standard document checklist for home loan applications',
  version: 1,
  isActive: true,
  categories: [
    {
      id: 'identity_docs',
      name: 'Identity Documents',
      description: 'KYC and identity verification documents',
      order: 0,
      items: [
        {
          id: 'pan_card',
          label: 'PAN Card',
          description: 'Valid PAN card of applicant and co-applicant',
          order: 0,
          isRequired: true,
          helpText: 'Self-attested copy required',
        },
        {
          id: 'aadhaar_card',
          label: 'Aadhaar Card',
          description: 'Aadhaar card of applicant and co-applicant',
          order: 1,
          isRequired: true,
          helpText: 'Front and back copy required',
        },
        {
          id: 'passport_photo',
          label: 'Passport Size Photos',
          description: '2 recent passport size photographs',
          order: 2,
          isRequired: true,
        },
        {
          id: 'voter_id',
          label: 'Voter ID / Passport / Driving License',
          description: 'Any one additional ID proof',
          order: 3,
          isRequired: false,
        },
      ],
    },
    {
      id: 'income_docs',
      name: 'Income Documents',
      description: 'Income proof and employment documents',
      order: 1,
      items: [
        {
          id: 'salary_slips',
          label: 'Salary Slips (Last 3 months)',
          description: 'Latest 3 months salary slips',
          order: 0,
          isRequired: true,
          helpText: 'For salaried applicants',
        },
        {
          id: 'bank_statements',
          label: 'Bank Statements (Last 6 months)',
          description: 'Salary account bank statements',
          order: 1,
          isRequired: true,
          helpText: 'All pages including transactions',
        },
        {
          id: 'form_16',
          label: 'Form 16',
          description: 'Form 16 for last 2 financial years',
          order: 2,
          isRequired: true,
          helpText: 'For salaried applicants',
        },
        {
          id: 'itr_returns',
          label: 'ITR Returns (Last 2 years)',
          description: 'Income Tax Returns with acknowledgment',
          order: 3,
          isRequired: true,
          helpText: 'Mandatory for self-employed',
        },
        {
          id: 'employment_letter',
          label: 'Employment Letter / Appointment Letter',
          description: 'Current employment proof',
          order: 4,
          isRequired: false,
        },
      ],
    },
    {
      id: 'property_docs',
      name: 'Property Documents',
      description: 'Property-related documents',
      order: 2,
      items: [
        {
          id: 'sale_agreement',
          label: 'Sale Agreement / Allotment Letter',
          description: 'Registered sale agreement or builder allotment letter',
          order: 0,
          isRequired: true,
        },
        {
          id: 'property_papers',
          label: 'Property Papers',
          description: 'Title deed, chain documents, encumbrance certificate',
          order: 1,
          isRequired: true,
        },
        {
          id: 'approved_plan',
          label: 'Approved Building Plan',
          description: 'Sanctioned building plan from local authority',
          order: 2,
          isRequired: true,
        },
        {
          id: 'noc_society',
          label: 'NOC from Society / Builder',
          description: 'No Objection Certificate',
          order: 3,
          isRequired: true,
        },
        {
          id: 'property_tax_receipt',
          label: 'Property Tax Receipt',
          description: 'Latest property tax receipt',
          order: 4,
          isRequired: false,
        },
        {
          id: 'occupancy_certificate',
          label: 'Occupancy Certificate',
          description: 'OC for completed properties',
          order: 5,
          isRequired: false,
          helpText: 'For ready properties only',
        },
      ],
    },
    {
      id: 'business_docs',
      name: 'Business Documents (Self-Employed)',
      description: 'For self-employed applicants',
      order: 3,
      items: [
        {
          id: 'business_proof',
          label: 'Business Proof',
          description: 'GST registration, Shop Act license, or Trade License',
          order: 0,
          isRequired: false,
          helpText: 'Required for self-employed',
        },
        {
          id: 'business_bank_statements',
          label: 'Business Bank Statements (Last 12 months)',
          description: 'Current account statements',
          order: 1,
          isRequired: false,
        },
        {
          id: 'partnership_deed',
          label: 'Partnership Deed / MOA-AOA',
          description: 'For partnership/company',
          order: 2,
          isRequired: false,
        },
        {
          id: 'balance_sheet',
          label: 'Balance Sheet & P&L (Last 2 years)',
          description: 'Audited financial statements',
          order: 3,
          isRequired: false,
        },
      ],
    },
    {
      id: 'additional_docs',
      name: 'Additional Documents',
      description: 'Other supporting documents',
      order: 4,
      items: [
        {
          id: 'cibil_consent',
          label: 'CIBIL Consent Form',
          description: 'Signed consent for credit check',
          order: 0,
          isRequired: true,
        },
        {
          id: 'processing_fee_receipt',
          label: 'Processing Fee Receipt',
          description: 'Proof of processing fee payment',
          order: 1,
          isRequired: false,
        },
        {
          id: 'insurance_documents',
          label: 'Insurance Documents',
          description: 'Life insurance, property insurance',
          order: 2,
          isRequired: false,
        },
      ],
    },
  ],
};

/**
 * LAP (Loan Against Property) Checklist
 * Similar to home loan but with slight differences
 */
const lapChecklist: Omit<ChecklistTemplate, 'id' | 'createdAt' | 'updatedAt'> = {
  ...homeLoanChecklist,
  loanType: 'lap' as LoanType,
  name: 'Loan Against Property Document Checklist',
  description: 'Standard document checklist for loan against property applications',
};

/**
 * Personal Loan Checklist Template
 */
const personalLoanChecklist: Omit<ChecklistTemplate, 'id' | 'createdAt' | 'updatedAt'> = {
  loanType: 'personal_loan' as LoanType,
  name: 'Personal Loan Document Checklist',
  description: 'Standard document checklist for personal loan applications',
  version: 1,
  isActive: true,
  categories: [
    {
      id: 'identity_docs',
      name: 'Identity Documents',
      order: 0,
      items: [
        {
          id: 'pan_card',
          label: 'PAN Card',
          description: 'Valid PAN card',
          order: 0,
          isRequired: true,
        },
        {
          id: 'aadhaar_card',
          label: 'Aadhaar Card',
          order: 1,
          isRequired: true,
        },
        {
          id: 'passport_photo',
          label: 'Passport Size Photos (2)',
          order: 2,
          isRequired: true,
        },
      ],
    },
    {
      id: 'income_docs',
      name: 'Income & Employment Documents',
      order: 1,
      items: [
        {
          id: 'salary_slips',
          label: 'Salary Slips (Last 3 months)',
          order: 0,
          isRequired: true,
        },
        {
          id: 'bank_statements',
          label: 'Bank Statements (Last 6 months)',
          order: 1,
          isRequired: true,
        },
        {
          id: 'form_16',
          label: 'Form 16 / ITR',
          description: 'Latest financial year',
          order: 2,
          isRequired: false,
        },
        {
          id: 'employment_letter',
          label: 'Employment Letter',
          description: 'Company ID card or offer letter',
          order: 3,
          isRequired: false,
        },
      ],
    },
    {
      id: 'address_docs',
      name: 'Address Proof',
      order: 2,
      items: [
        {
          id: 'address_proof',
          label: 'Address Proof',
          description: 'Utility bill, rent agreement, or Aadhaar',
          order: 0,
          isRequired: true,
        },
      ],
    },
    {
      id: 'additional_docs',
      name: 'Additional Documents',
      order: 3,
      items: [
        {
          id: 'cibil_consent',
          label: 'CIBIL Consent Form',
          order: 0,
          isRequired: true,
        },
        {
          id: 'processing_fee_receipt',
          label: 'Processing Fee Receipt',
          order: 1,
          isRequired: false,
        },
      ],
    },
  ],
};

/**
 * Business Loan Checklist Template
 */
const businessLoanChecklist: Omit<ChecklistTemplate, 'id' | 'createdAt' | 'updatedAt'> = {
  loanType: 'business_loan' as LoanType,
  name: 'Business Loan Document Checklist',
  description: 'Standard document checklist for business loan applications',
  version: 1,
  isActive: true,
  categories: [
    {
      id: 'identity_docs',
      name: 'Identity Documents',
      order: 0,
      items: [
        {
          id: 'pan_card',
          label: 'PAN Card (Proprietor/Partners/Directors)',
          order: 0,
          isRequired: true,
        },
        {
          id: 'aadhaar_card',
          label: 'Aadhaar Card',
          order: 1,
          isRequired: true,
        },
        {
          id: 'passport_photo',
          label: 'Passport Size Photos',
          order: 2,
          isRequired: true,
        },
      ],
    },
    {
      id: 'business_docs',
      name: 'Business Registration Documents',
      order: 1,
      items: [
        {
          id: 'business_pan',
          label: 'Business PAN Card',
          order: 0,
          isRequired: true,
        },
        {
          id: 'gst_registration',
          label: 'GST Registration Certificate',
          order: 1,
          isRequired: true,
        },
        {
          id: 'business_registration',
          label: 'Business Registration Certificate',
          description: 'Shop Act, MSME, Trade License',
          order: 2,
          isRequired: true,
        },
        {
          id: 'partnership_deed',
          label: 'Partnership Deed / MOA-AOA',
          description: 'For partnership/company',
          order: 3,
          isRequired: false,
        },
      ],
    },
    {
      id: 'financial_docs',
      name: 'Financial Documents',
      order: 2,
      items: [
        {
          id: 'bank_statements',
          label: 'Business Bank Statements (Last 12 months)',
          order: 0,
          isRequired: true,
        },
        {
          id: 'itr_returns',
          label: 'ITR Returns (Last 3 years)',
          description: 'Business and personal ITR',
          order: 1,
          isRequired: true,
        },
        {
          id: 'balance_sheet',
          label: 'Balance Sheet & P&L (Last 3 years)',
          description: 'Audited financial statements',
          order: 2,
          isRequired: true,
        },
        {
          id: 'gst_returns',
          label: 'GST Returns (Last 12 months)',
          order: 3,
          isRequired: true,
        },
      ],
    },
    {
      id: 'property_docs',
      name: 'Collateral Documents (if applicable)',
      order: 3,
      items: [
        {
          id: 'property_papers',
          label: 'Property Papers',
          description: 'If offering property as collateral',
          order: 0,
          isRequired: false,
        },
        {
          id: 'machinery_invoice',
          label: 'Machinery Invoice / Hypothecation',
          description: 'If offering machinery as collateral',
          order: 1,
          isRequired: false,
        },
      ],
    },
    {
      id: 'additional_docs',
      name: 'Additional Documents',
      order: 4,
      items: [
        {
          id: 'business_plan',
          label: 'Business Plan / Project Report',
          order: 0,
          isRequired: false,
        },
        {
          id: 'cibil_consent',
          label: 'CIBIL Consent Form',
          order: 1,
          isRequired: true,
        },
        {
          id: 'processing_fee_receipt',
          label: 'Processing Fee Receipt',
          order: 2,
          isRequired: false,
        },
      ],
    },
  ],
};

/**
 * Car Loan Checklist Template
 */
const carLoanChecklist: Omit<ChecklistTemplate, 'id' | 'createdAt' | 'updatedAt'> = {
  loanType: 'car_loan' as LoanType,
  name: 'Car Loan Document Checklist',
  description: 'Standard document checklist for car loan applications',
  version: 1,
  isActive: true,
  categories: [
    {
      id: 'identity_docs',
      name: 'Identity Documents',
      order: 0,
      items: [
        {
          id: 'pan_card',
          label: 'PAN Card',
          order: 0,
          isRequired: true,
        },
        {
          id: 'aadhaar_card',
          label: 'Aadhaar Card',
          order: 1,
          isRequired: true,
        },
        {
          id: 'passport_photo',
          label: 'Passport Size Photos (2)',
          order: 2,
          isRequired: true,
        },
        {
          id: 'driving_license',
          label: 'Driving License',
          order: 3,
          isRequired: true,
        },
      ],
    },
    {
      id: 'income_docs',
      name: 'Income Documents',
      order: 1,
      items: [
        {
          id: 'salary_slips',
          label: 'Salary Slips (Last 3 months)',
          order: 0,
          isRequired: true,
        },
        {
          id: 'bank_statements',
          label: 'Bank Statements (Last 6 months)',
          order: 1,
          isRequired: true,
        },
        {
          id: 'form_16',
          label: 'Form 16 / ITR',
          order: 2,
          isRequired: false,
        },
      ],
    },
    {
      id: 'vehicle_docs',
      name: 'Vehicle Documents',
      order: 2,
      items: [
        {
          id: 'vehicle_proforma',
          label: 'Proforma Invoice from Dealer',
          description: 'Quotation showing vehicle details and price',
          order: 0,
          isRequired: true,
        },
        {
          id: 'vehicle_registration',
          label: 'RC (Registration Certificate)',
          description: 'For used cars only',
          order: 1,
          isRequired: false,
          helpText: 'Required for used car loans',
        },
        {
          id: 'insurance_copy',
          label: 'Insurance Copy',
          description: 'For used cars',
          order: 2,
          isRequired: false,
        },
      ],
    },
    {
      id: 'address_docs',
      name: 'Address Proof',
      order: 3,
      items: [
        {
          id: 'address_proof',
          label: 'Address Proof',
          description: 'Utility bill, rent agreement',
          order: 0,
          isRequired: true,
        },
      ],
    },
    {
      id: 'additional_docs',
      name: 'Additional Documents',
      order: 4,
      items: [
        {
          id: 'cibil_consent',
          label: 'CIBIL Consent Form',
          order: 0,
          isRequired: true,
        },
        {
          id: 'down_payment_receipt',
          label: 'Down Payment Receipt',
          order: 1,
          isRequired: false,
        },
      ],
    },
  ],
};

// ============================================================================
// MIGRATION RESULT INTERFACE
// ============================================================================

interface MigrationResult {
  success: boolean;
  templatesCreated: number;
  errors: string[];
  message: string;
}

// ============================================================================
// MIGRATION FUNCTION
// ============================================================================

/**
 * Run the migration to create checklist templates in Firebase
 */
export const runMigration = async (): Promise<MigrationResult> => {
  const result: MigrationResult = {
    success: true,
    templatesCreated: 0,
    errors: [],
    message: '',
  };

  console.log('🚀 Starting checklist template migration...\n');

  const templates = [
    homeLoanChecklist,
    lapChecklist,
    personalLoanChecklist,
    businessLoanChecklist,
    carLoanChecklist,
  ];

  try {
    for (const template of templates) {
      try {
        console.log(`  📝 Migrating ${template.name}...`);
        
        await createChecklistTemplate(template);
        
        result.templatesCreated++;
        console.log(`  ✅ ${template.loanType} migrated successfully\n`);
      } catch (error: any) {
        const errorMsg = `Failed to migrate ${template.loanType}: ${error.message}`;
        result.errors.push(errorMsg);
        console.error(`  ❌ ${errorMsg}\n`);
      }
    }

    console.log('\n📊 Migration Summary:');
    console.log(`   ✅ Successfully created: ${result.templatesCreated} templates`);
    if (result.errors.length > 0) {
      console.log(`   ❌ Errors: ${result.errors.length}`);
      result.errors.forEach(error => console.log(`      - ${error}`));
      result.success = false;
    }

    result.message = result.success
      ? `✨ Migration complete! Created ${result.templatesCreated} checklist templates.`
      : `⚠️ Migration completed with errors. Created ${result.templatesCreated} templates, ${result.errors.length} failed.`;

    console.log(`\n${result.message}`);
  } catch (error: any) {
    result.success = false;
    result.message = `❌ Migration failed: ${error.message}`;
    result.errors.push(error.message);
    console.error('\n❌ Migration failed:', error);
  }

  return result;
};

// ============================================================================
// BROWSER GLOBAL EXPOSURE
// ============================================================================

/**
 * Export for use in browser console
 */
export const migrateChecklistTemplates = runMigration;

// Make available on window object for easy console access
if (typeof window !== 'undefined') {
  (window as unknown as { runChecklistMigration: typeof runMigration }).runChecklistMigration = runMigration;
  (window as unknown as { migrateChecklistTemplates: typeof runMigration }).migrateChecklistTemplates = runMigration;
}
